import { Component, ViewChild } from "@angular/core";
import { ImportsModule } from "../imports";

import { ActivatedRoute, Route } from "@angular/router";
import { ScopeService } from "../services/scope.service";
import { MessagingService } from "../services/messaging.service";
import { Table, TableLazyLoadEvent } from "primeng/table";

interface Column {
  actions?: any[];
  key?: boolean;
  label: string;
  field: string;
  width?: number;
  align?: string;
  options?: any[];
  filterValue?: any;
  maxLength?: number,
  upperCase?: boolean;
  hidden?: boolean;
  filterType?: string;
  matchMode?: string;
  editable?: boolean;
  title?: string;
  getText?: boolean;
}

@Component({
  selector: "app-all-objects",
  standalone: true,
  imports: [ImportsModule],
  templateUrl: "./all-objects.component.html",
  styleUrl: "./all-objects.component.css",
})

export class AllObjectsComponent {
  @ViewChild('dt') dataTable!: Table;
  refreshButtonText: { label: string; }[] = [{ label: "Refresh" }, { label: "Clear Filters" }];
  types: { label: string, value: any; }[] = [{ label: "Refactor", value: "Refactor" }, { label: "Rewrite", value: "Rewrite" }, { label: "Retain", value: "Retain" }, { label: "Retire", value: "Retire" }, { label: "Replace", value: "Replace" }, { label: "Advised", value: null }];
  hasDisplayTypes: { label: string, value: any; }[] = [{ label: "Yes", value: true }, { label: "No", value: false }];
  srcMatchTypes: { label: string, value: any; }[] = [{ label: "Exact", value: "Exact" }, { label: "Override Found", value: "Override Found" }, { label: "Different Dates", value: "Different Dates" }, { label: "Different File", value: "Different File" }, { label: "Different Library", value: "Different Library" }, { label: "Missing Source", value: "Missing source" }];
  hasSourceTypes: { label: string, value: any; }[] = [{ label: "All", value: null }, { label: "Found", value: true }, { label: "Missing", value: false }];
  libAppAreaTypes!: any[];
  appAreaTypes!: any[];
  libraryNames!: any[];
  lazyRows: number = 100;
  isFiltered: boolean = false;
  columns!: Column[];
  items: any[] = [];
  totalRecords!: number;
  isBusy: boolean = true;
  sortedColumns: any[] = [];
  defaultFilters!: any;
  priorFilterAndSort!: { filters: any, orderby: any; };
  massEditVisible: boolean = false;
  massEditProps!: any[];
  phaseOptions!: any[];
  private refreshing: boolean = false;
  private isInitEditing: boolean = false;

  constructor(private scopingService: ScopeService, private messagingService: MessagingService, private router: ActivatedRoute) {
  }

  ngOnInit() {
    const viewType = this.router.snapshot.data["viewType"];
    const isScopingView = viewType === "Scoping";
    const isSourceResolutionView = viewType === "SourceResolution";
    const isObjectLevelOverrideView = viewType === "ObjectLevelOverride";

    if (isScopingView) {
      this.defaultFilters = {
        libType: { matchMode: "equals", value: 3 },
        type: { matchMode: "contains", value: "PGM" }
      };
    }

    if (isSourceResolutionView) {
      this.srcMatchTypes.shift();
      this.srcMatchTypes.shift();

      this.defaultFilters = {
        libType: { matchMode: "equals", value: 3 },
        objTarget: { matchMode: "in", value: ["Refactor", "Rewrite"] },
        srcMatch: { matchMode: "notEquals", value: "Exact" },
      };
    }

    if (isObjectLevelOverrideView) {
      this.defaultFilters = {
        libType: { matchMode: "equals", value: 3 }
      };
    }

    if (!isScopingView) {
      this.sortedColumns.push({ field: 'library', order: 1 }, { field: 'name', order: 1 }, { field: 'type', order: 1 }, { field: 'attribute', order: 1 });
    } else {
      this.sortedColumns.push({ field: 'library', order: 1 }, { field: 'name', order: 1 }, { field: 'attribute', order: 1 });
    }

    this.priorFilterAndSort = { filters: this.defaultFilters, orderby: this.sortedColumns };

    this.scopingService.getAppAreas({ orderby: "area" }).subscribe({
      next: (data: any) => {
        this.libAppAreaTypes = data.items.map((i: any) => {
          const item = { label: i.area, value: i.id };
          if (i.text) item.label += ` - ${i.text}`;
          return item;
        });
        this.appAreaTypes = this.libAppAreaTypes.concat([{ label: "Other", value: null }]);
        this.libAppAreaTypes.push({ label: "Other", value: null });

        const parms = {
          filters: {
            type: { matchMode: "equals", value: 3 }
          },
          orderby: "name"
        };

        this.scopingService.getScopePhases({ orderby: "phase"}).subscribe({
          next: (data: any) => {
            this.phaseOptions = data.items.filter((item: { phase: null; }) => item.phase !== null)
            .map((item: { phase: any; }) => ({
              label: item.phase, value: item.phase
            }));
            this.phaseOptions.push({label: "All", value: null})

            this.scopingService.getLibraries(parms).subscribe({
              next: (data: any) => {
                this.libraryNames = data.items.map((i: any) => { return { label: i.name, value: i.name }; });
                const massEditProps: any[] = [];

                const columns: any[] = [
                  { field: "id", label: "id", hidden: true, key: true },
                  { field: "libType", label: "Library Type", hidden: true, width: 200, filterType: "numeric", align: "right" },
                  { field: "library", label: "Library", width: 250, options: this.libraryNames, matchMode: "in" },
                  { field: "name", label: "Name", width: 250, maxLength: 10, upperCase: true },
                  { field: "type", hidden: isScopingView, label: "Type", width: 200, maxLength: 10, upperCase: true, matchMode: "contains" },
                  { field: "attribute", label: "Attribute", width: 175, maxLength: 10, upperCase: true },
                  { field: "text", label: "Text", width: 400, maxLength: 50, matchMode: "contains" },
                ];


                if (isScopingView) {
                  columns.push(
                    { field: "lastActivity", label: "Last Activity", width: 215, filterType: "date", align: "right", matchMode: "dateAfter", title: "Last date the object was used, restored, changed or created." },
                    { field: "lastUsed", label: "Last Used", width: 215, filterType: "date", align: "right", matchMode: "dateAfter" },
                    { field: "created", label: "Created", width: 225, filterType: "date", align: "right", matchMode: "dateAfter" },
                    { field: "changed", label: "Changed", width: 225, filterType: "date", align: "right", matchMode: "dateAfter" },
                    { field: "hasSource", label: "Source", width: 200, options: this.hasSourceTypes, matchMode: "equals" },
                    { field: "hasDisplay", label: "Has Screen", width: 200, options: this.hasDisplayTypes, matchMode: "in", getText: true },
                    { field: "srcLines", label: "LOC", width: 200, filterType: "numeric", align: "right", matchMode: "gte" },
                    { field: "complexity", label: "Complexity Rating", width: 200, filterType: "numeric", align: "right", matchMode: "gte", hidden: true },
                    { field: "advisedReason", label: "Advised Reason", width: 200, maxLength: 200, matchMode: "contains" }, 
                    { field: "advisedTarget", label: "Advised Target", width: 200, options: this.types, matchMode: "in", getText: true },
                    { field: "target", label: "Target", width: 200, options: this.types, matchMode: "in", editable: true, getText: true },
                    { field: "reason", label: "Target Reason", width: 400, maxLength: 200, matchMode: "contains", editable: true },
                    { field: "phase",  label: "Phase Override", width:50, options: this.phaseOptions, matchMode: "equals", align: "right", editable: true, filterType: "numeric" },
                    { field: "appareaphase",  label: "Application Area Phase", width:50,  options: this.phaseOptions, maxLength: 200, matchMode: "equals", align: "right", editable: false, filterType: "numeric"  },
                    { field: "notes", label: "Notes", width: 400, maxLength: 200, matchMode: "contains", editable: true },
                  );

                  massEditProps.push(
                    { field: "target", label: "Replace Target", change: false, options: this.types, value: null, default: this.types[0].value },
                    { field: "reason", label: "Replace Reason", change: false, maxLength: 1000, multiline: true, value: null, default: null },
                    { field: "notes", label: "Replace Notes", maxLength: 1000, change: false, multiline: true, value: null, default: null },
                    { field: "phase",  label: "Phase Override", maxLength: 10, change: false, value: null, default: null, type: "numeric"},
                  );
                }

                if (isSourceResolutionView) {
                  columns.push(
                    { field: "srcMatch", label: "Source Match", width: 250, matchMode: "contains", options: this.srcMatchTypes, getText: true },
                    { field: "objSrcLibrary", label: "Object Source Library", width: 200, upperCase: true, maxLength: 10 },
                    { field: "ovrSrcLibrary", label: "Override Source Library", width: 200, upperCase: true, maxLength: 10, editable: true },
                    { field: "srcLibrary", label: "Target Source Library", width: 200, upperCase: true, maxLength: 10 },
                    { field: "objSrcFile", label: "Object Source File", width: 200, upperCase: true, maxLength: 10 },
                    { field: "ovrSrcFile", label: "Override Source File", width: 200, upperCase: true, maxLength: 10, editable: true },
                    { field: "srcFile", label: "Target Source File", width: 200, upperCase: true, maxLength: 10 },
                    { field: "objSrcMember", label: "Object Source Member", width: 200, upperCase: true, maxLength: 10 },
                    { field: "ovrSrcMember", label: "Override Source Member", width: 200, upperCase: true, maxLength: 10, editable: true },
                    { field: "srcMember", label: "Target Source Member", width: 200, upperCase: true, maxLength: 10 },
                    { field: "objSrcDate", label: "Object Source Date", width: 225, filterType: "date", align: "right", matchMode: "dateAfter" },
                    { field: "srcDate", label: "Target Source Date", width: 225, filterType: "date", align: "right", matchMode: "dateAfter" },
                    { field: "phase",  label: "Phase Override", width:50, options: this.phaseOptions, matchMode: "equals", align: "right", editable: true, filterType: "numeric" },
                    { field: "appareaphase",  label: "Application Area Phase", width:50,  options: this.phaseOptions, maxLength: 200, matchMode: "equals", align: "right", editable: false, filterType: "numeric"  },
                    { field: "notes", label: "Notes", width: 400, maxLength: 200, matchMode: "contains", editable: true },
                  );

                  massEditProps.push(
                    { field: "ovrSrcLibrary", label: "Override Source Library", maxLength: 10, upperCase: true, change: false, value: null, default: null },
                    { field: "ovrSrcFile", label: "Override Source File", maxLength: 10, upperCase: true, change: false, value: null, default: null },
                    { field: "ovrSrcMember", label: "Override Source Member", maxLength: 10, upperCase: true, change: false, value: null, default: null },
                    { field: "notes", label: "Replace Notes", maxLength: 1000, change: false, multiline: true, value: null, default: null },
                    { field: "phase",  label: "Phase Override", maxLength: 10, change: false, value: null, default: null, type: "numeric"},
                  );
                }

                if (isObjectLevelOverrideView) {
                  columns.push(
                    { field: "libAppArea", label: "Library Application Area", width: 275, options: this.libAppAreaTypes, matchMode: "in", getText: true },
                    { field: "appArea", label: "Override Application Area", width: 275, options: this.appAreaTypes, matchMode: "in", editable: true, getText: true },
                    { field: "srcLibrary", label: "Target Source Library", width: 250, upperCase: true, maxLength: 10 },
                    { field: "srcFile", label: "Target Source File", width: 250, upperCase: true, maxLength: 10 },
                    { field: "objTarget", label: "Object Target", width: 200, options: this.types, matchMode: "in", getText: true },
                    { field: "advisedReason", label: "Advised Reason", width: 300, maxLength: 300, matchMode: "contains" }, 
                    { field: "phase",  label: "Phase Override", width:50, options: this.phaseOptions, matchMode: "equals", align: "right", editable: true, filterType: "numeric" },
                    { field: "appareaphase",  label: "Application Area Phase", width:50,  options: this.phaseOptions, maxLength: 200, matchMode: "equals", align: "right", editable: false, filterType: "numeric"  },
                    { field: "notes", label: "Notes", width: 400, maxLength: 200, matchMode: "contains", editable: true },
                  );
                  
                  massEditProps.push(
                    { field: "appArea", label: "Override Application Area", change: false, options: this.appAreaTypes, value: null, default: null },
                    { field: "notes", label: "Replace Notes", maxLength: 1000, change: false, multiline: true, value: null, default: null },
                    { field: "phase",  label: "Phase Override", maxLength: 10, change: false, value: null, default: null, type: "numeric"},
                  );

                }

                this.columns = columns;
                this.massEditProps = massEditProps;
                this.refresh();
              },
              error: (error: any) => {
                this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
              }
            });
          }
        })
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      }
    });
  }

  refresh() {
    this.isBusy = true;
    this.refreshing = true;
    this.priorFilterAndSort.filters = this.defaultFilters;
    if (this.dataTable) this.dataTable.resetScrollTop();
    this.getMoreRecords();
  }

  getFilter(event?: any): any {
    if (!event) return Object.assign({ changed: true }, this.priorFilterAndSort);

    // We only want to show them scoped libraries (for now) and programs
    const filters = JSON.parse(JSON.stringify(this.defaultFilters));

    // Add/replace more filters based on user input
    this.isFiltered = false;
    if (event.filters) {
      for (const field in event.filters) {
        const filter: any = event.filters[field] || null;
        if (!filter) continue;
        if (filter.value == null) continue;
        if (this.refreshing) {
          const defaultValue = this.defaultFilters[filter.name]?.value || null;
          const column = this.columns.find((c: Column) => c.field === field);
          if (column && column.filterValue) column.filterValue = defaultValue;
          filter.value = defaultValue;
        } else {
          if (typeof filter.value === "string" && filter.value.length == 0) continue;
          if (typeof filter.value === "number" && isNaN(filter.value)) continue;
          if (Array.isArray(filter.value) && !filter.value.length) continue;
          const column = this.columns.find((c: any) => c.field === field);
          if (column?.upperCase) filter.value = filter.value.toUpperCase();
          if (column?.filterType === "numeric") filter.value = parseInt(filter.value);
          this.isFiltered = true;
          filters[field] = Object.assign({}, event.filters[field]);
        }
      }
    }

    if (this.refreshing) this.refreshing = false;

    // Add sorting
    let orderby: any = null;
    if (event?.multiSortMeta?.length) {
      orderby = [];
      for (const column of event.multiSortMeta) {
        const col = this.columns.find((c: any) => c.field === column.field);
        if (col) {
          orderby.push({ field: col.field, order: column.order });
        }
      }
    } else orderby = this.priorFilterAndSort.orderby;

    const result: any = { filters: filters, orderby: orderby };

    // compare filters and orderyby to prior values
    const changed = this.isBusy || JSON.stringify(result) !== JSON.stringify(this.priorFilterAndSort);
    this.priorFilterAndSort = JSON.parse(JSON.stringify(result));
    this.isBusy = result.changed = changed;

    return result;
  }

  getMoreRecords(event?: TableLazyLoadEvent) {
    this.isBusy = true;
    const filter = this.getFilter(event);
    let first = event?.first || 0;
    let rows = event?.rows || this.lazyRows;

    filter.select = this.columns.map((c: any) => c.field);
    filter.count = first == 0;
    filter.skip = first;
    filter.limit = rows;
    filter.orderby = filter.orderby.map((o: any) => { return { field: o.field, desc: o.order < 0 ? true : undefined }; });

    this.scopingService.getObjects(filter).subscribe({
      next: (data: any) => {
        const items: any[] = data.items;
        items.filter(i => i.lastTargetValue = i.target);
        items.filter(i => i.hasSource = i.hasSource ? "Found" : i.hasSource === null ? "" : "Missing");
        if (filter.count) {
          this.totalRecords = data.totalRecords;
          // this.items = items.concat(Array.from({ length: this.totalRecords - items.length }));
        } else {
          // Array.prototype.splice.apply(this.items, [first, items.length, ...items]);
        }
        this.items = items;
        if (event?.forceUpdate) event.forceUpdate();
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      },
      complete: () => {
        this.isBusy = false;
      }
    });
  }

  updateRecord(data: any) {
    // Only send the fields that are key or editable
    const fields = this.columns.filter((c: any) => c.key | c.editable);
    const changedObject: any = {};
    for (const field of fields) {
      if (data[field.field] === null)
        changedObject[field.field] = null
      else {
        if (field.filterType === "numeric" && data[field.field].length === 0) 
          changedObject[field.field] = null
        else
          changedObject[field.field] = data[field.field];
      }
    }

    if (data.lastTargetValue !== data.target) {
      changedObject.targetLastChangedBy = "user";
      changedObject.targetLastChangedDate = new Date();
    }
    changedObject.lastChangedBy = "user";
    changedObject.lastChangedDate = new Date();
    this.scopingService.updateObject(changedObject).subscribe({
      next: (result: any) => {
        // get item from items and update it
        const item = this.items.find((i: any) => i.id === data.id);
        if (item) {
          item.lastTargetValue = item.target;
          for (const field of fields) {
            item[field.field] = data[field.field];
          }
        }
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to update record.", error);
      }
    });
  }

  exportExcel() {
    this.isBusy = true;
    this.scopingService.getObjects(this.priorFilterAndSort).subscribe({
      next: (data: any) => {
        const items: any[] = data.items || data || [];
        import("xlsx").then(xlsx => {
          const worksheet = xlsx.utils.json_to_sheet(items);
          const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
          const excelBuffer: any = xlsx.write(workbook, {
            bookType: "xlsx",
            type: "array"
          });
          this.saveAsExcelFile(excelBuffer, "objects");
          this.items = data.items;
        });
      }
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver-es").then(FileSaver => {
      this.isBusy = false;
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });
      FileSaver.saveAs(
        data,
        fileName + EXCEL_EXTENSION
      );
    });
  };

  getRefreshText() {
    return !this.isFiltered ? this.refreshButtonText[0].label : this.refreshButtonText[1].label;
  }

  showMassEdit() {
    this.massEditProps.filter(p => p.change = false);
    this.massEditProps.filter(p => p.value = p.default);
    this.massEditVisible = true;
  }

  canDoMassUpdate() {
    return this.massEditProps.some((p: any) => p.change);
  }

  doMassUpdate() {
    this.isBusy = true;
    const parms = this.getFilter();
    parms.newValues = {};
    const newValues = this.massEditProps.filter((p: any) => p.change);
    for (const field of newValues) {
      if (field.value == null || !field.value.trim()) field.value = null;

      if (field.upperCase) field.value = field.value.toUpperCase();
      if (field.type === "numeric" && field.value != null) parms.newValues[field.field] = Number(field.value);
      else parms.newValues[field.field] = field.value;
    }

    this.scopingService.massUpdateObjects(parms).subscribe({
      next: (data: any) => {
        for (const field in parms.newValues) {
          this.items.filter(i => i[field] = parms.newValues[field]);
          if (field === 'phase')
            this.items.filter(i => i['objPhase'] = parms.newValues[field]);
        }

      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      },
      complete: () => {
        this.isBusy = false;
        this.massEditVisible = false;
      }
    });
  }

  getEditingItemsText(prefix: string) {
    if (!this.isFiltered) return prefix + ' ALL items';
    else {
      let result = prefix + ` ${this.totalRecords} item`;
      if (this.totalRecords !== 1) result += "s";
      return result;
    }
  }

  doAction(action: any, data: any) {
    if (typeof action == "function") action.call(this, data);
  }

  getText(item: any, column: any) {
    return column.options.find((o: any) => o.value === item[column.field])?.label;
  }

  onEditInit(event: any) {
    this.isInitEditing = true;
  }
  onEditComplete(event: any) {
    this.isInitEditing = false;
  }

  dropDownOnFocus(dropDown: any) {
    if (this.isInitEditing && dropDown.focused) {
      this.isInitEditing = false;
      dropDown.hide();
      dropDown.show();
    }
  }
}
